import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import standardBizApiClient from '../../../util/BizApi/standardBizApiClient';
import './LeaderBoard.css';
import Image from '../../../components/image/Image';

function getMonthsFromOptInToCurrent(arenaOpts, certCode) {
  if (!arenaOpts || !certCode || !Array.isArray(arenaOpts)) return [];

  // Find the earliest created_dt for the given certCode where opt_state is 'in'
  let earliestDate = null;
  arenaOpts.forEach(({ arena_code, created_dt, opt_state }) => {
    if (arena_code?.toUpperCase() === certCode.toUpperCase() && opt_state === 'in' && created_dt) {
      try {
        const date = new Date(created_dt);
        if (!earliestDate || date < earliestDate) {
          earliestDate = date;
        }
      } catch (err) {
        console.error('Invalid created_dt format:', created_dt, err);
      }
    }
  });

  if (!earliestDate) return [];

  // Generate months from earliestDate to current month
  const currentDate = new Date();
  const months = [];
  let current = new Date(earliestDate.getFullYear(), earliestDate.getMonth(), 1);

  while (current <= currentDate) {
    const year = current.getFullYear();
    const month = String(current.getMonth() + 1).padStart(2, '0');
    months.push(`${year}-${month}`);
    current.setMonth(current.getMonth() + 1);
  }

  return months.sort().reverse();
}

/**
 * Custom hook to manage opt-in state (fetch + toggle + submit)
 */
function useOptInState(certCode, userId) {
  const [isOptedIn, setIsOptedIn] = useState(false);
  const [userName, setUserName] = useState('');
  const [arenaOpts, setArenaOpts] = useState([]);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [userNameError, setUserNameError] = useState('');
  const [showNameModal, setShowNameModal] = useState(false);
  const [showOptOutConfirm, setShowOptOutConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch the user's current opt-in status and (global) display name
  const fetchOptInState = useCallback(async () => {
    if (!certCode || !userId) {
      setIsOptedIn(false);
      setArenaOpts([]);
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const resp = await standardBizApiClient.scoress_leaderboard_get_entity(userId, 'wnp');
      const arenaOpts = resp?.leaderboard?.arena_opt || [];
      const match = arenaOpts.find(({ arena_code }) => arena_code?.toUpperCase() === certCode.toUpperCase());

      const globalName = resp?.display_name || '';
      const optedInForCert = match?.opt_state === 'in';

      setIsOptedIn(optedInForCert);
      setArenaOpts(arenaOpts);
      if (globalName) {
        setUserName(globalName);
      } else if (!optedInForCert) {
        setUserName('');
      }

      setError(null);
      return optedInForCert;
    } catch (err) {
      console.error('Failed to fetch opt-in state:', err);
      setError('Could not retrieve opt-in status.');
      setIsOptedIn(false);
      setUserName('');
      setArenaOpts([]);
      return false;
    } finally {
      setLoading(false);
    }
  }, [certCode, userId]);

  // Submit name → create_entity if needed → opt-in
  const submitNameAndOptIn = async () => {
    const trimmed = userName.trim();
    if (!trimmed) {
      setUserNameError('Please enter your name.');
      return;
    }
    if (/\d/.test(trimmed)) {
      setUserNameError('Username cannot contain numbers.');
      return;
    }
    if (!agreedToTerms) {
      setUserNameError('Please agree to Terms & Conditions.');
      return;
    }

    setUserNameError('');
    setLoading(true);
    setError(null);

    try {
      await standardBizApiClient.scoress_leaderboard_create_entity(userId, 'wnp', trimmed);
      await standardBizApiClient.scoress_leaderboard_opt_in(userId, 'wnp', certCode);

      setIsOptedIn(true);
      setShowNameModal(false);
      setAgreedToTerms(false);
      await fetchOptInState();
    } catch (err) {
      console.error('Error opting in:', err);
      setError('Unable to opt-in right now. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Confirm opt‐out
  const confirmOptOut = async () => {
    if (!certCode || !userId) return;
    setLoading(true);
    setError(null);

    try {
      await standardBizApiClient.scoress_leaderboard_opt_out(userId, 'wnp', certCode);
      setIsOptedIn(false);
      setShowOptOutConfirm(false);
      setArenaOpts((prev) =>
        prev.filter(({ arena_code }) => arena_code?.toUpperCase() !== certCode.toUpperCase())
      );
    } catch (err) {
      console.error('Error opting out:', err);
      setError('Unable to opt-out right now. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handler for the toggle switch
  const handleToggle = () => {
    if (!certCode || !userId || loading) return;
    if (isOptedIn) {
      setShowOptOutConfirm(true);
    } else {
      setUserNameError('');
      setAgreedToTerms(false);
      setShowNameModal(true);
    }
  };

  // Whenever certCode or userId changes, re-fetch the opt-in state
  useEffect(() => {
    fetchOptInState();
  }, [fetchOptInState]);

  return {
    isOptedIn,
    userName,
    setUserName,
    arenaOpts,
    agreedToTerms,
    setAgreedToTerms,
    userNameError,
    setUserNameError,
    showNameModal,
    showOptOutConfirm,
    loading,
    error,
    handleToggle,
    submitNameAndOptIn,
    confirmOptOut,
    cancelOptOut: () => setShowOptOutConfirm(false),
    cancelOptIn: () => setShowNameModal(false),
  };
}

/**
 * Custom hook to fetch leaderboard data (all-time or monthly based on selectedMonth)
 */
function useLeaderboardData(certCode, userId, isOptedIn, selectedMonth) {
  const [apiData, setApiData] = useState([]);
  const [podium, setPodium] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Only fetch if isOptedIn === true
  const fetchLeaderboard = useCallback(async () => {
    if (!certCode || !userId || !isOptedIn) {
      // If not opted in, do not call the API at all; just clear locally stored data.
      setApiData([]);
      setPodium([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let resp;
      if (selectedMonth) {
        const [year, month] = selectedMonth.split('-');
        const monthStartDate = `${year}-${month}-01`;
        const lastDay = new Date(year, month, 0).getDate(); 
        const monthEndDateLocal = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
        const monthEndDate = monthEndDateLocal;
        resp = await standardBizApiClient.scoress_get_leaderboard_monthly(
          'wnp',
          certCode,
          monthStartDate,
          monthEndDate,
          'full'
        );
      } else {
        resp = await standardBizApiClient.scoress_get_leaderboard('wnp', certCode, 'full');
      }

      const parsed = (resp?.ranks || []).map((entry) => ({
        rank: entry.ordinal_position,
        username: entry.display_name,
        score: entry.calc_detail?.factor ?? entry.score ?? 0,
        initial: entry.display_name ? entry.display_name.charAt(0).toUpperCase() : '',
        entityRef: entry.entity_ref,
        isCurrentUser: entry.entity_ref === userId,
      }));      
      setApiData(parsed);
      const topThree = parsed
        .filter((p) => p.rank >= 1 && p.rank <= 3)
        .sort((a, b) => a.rank - b.rank);
      setPodium(topThree);
    } catch (err) {
      console.error('Failed to fetch leaderboard:', err);
      setError('Could not load leaderboard. Please try again later.');
      setApiData([]);
      setPodium([]);
    } finally {
      setLoading(false);
    }
  }, [certCode, userId, isOptedIn, selectedMonth]);

  // Only run fetchLeaderboard() when isOptedIn turns true (or when certCode/userId/isOptedIn changes).
  useEffect(() => {
    if (isOptedIn) {
      fetchLeaderboard();
    } else {
      // If user is not opted in, ensure both arrays stay empty
      setApiData([]);
      setPodium([]);
    }
  }, [isOptedIn, selectedMonth, fetchLeaderboard]);

  return {
    apiData,
    podium,
    loading,
    error,
  };
}

/**
 * Main LeaderBoard component
 */
const LeaderBoard = () => {
  const certCodeInFocus = useSelector((state) => state.practice.certCodeInFocus);
  const userId = useSelector((state) => state.user.userId);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [expandedYear, setExpandedYear] = useState(new Date().getFullYear());
  const [hasUserInteractedWithMonthly, setHasUserInteractedWithMonthly] = useState(false);

  const {
    isOptedIn,
    userName,
    setUserName,
    arenaOpts,
    agreedToTerms,
    setAgreedToTerms,
    userNameError,
    setUserNameError,
    showNameModal,
    showOptOutConfirm,
    loading: optInLoading,
    error: optInError,
    handleToggle,
    submitNameAndOptIn,
    confirmOptOut,
    cancelOptOut,
    cancelOptIn,
  } = useOptInState(certCodeInFocus, userId);

  const {
    apiData: apiLeaderboardData,
    podium: podiumUsers,
    loading: boardLoading,
    error: boardError,
  } = useLeaderboardData(certCodeInFocus, userId, isOptedIn, selectedMonth);

  // Combine loading + error from both hooks
  const loading = optInLoading || boardLoading;
  const error = optInError || boardError;

  const availableMonths = useMemo(
    () => getMonthsFromOptInToCurrent(arenaOpts, certCodeInFocus),
    [arenaOpts, certCodeInFocus]
  );

  // Get current month in YYYY-MM format
  const getCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  // Handle monthly button click - set current month if no month is selected (first time)
  const handleMonthlyClick = () => {
    if (!hasUserInteractedWithMonthly && availableMonths.length > 0) {
      const currentMonth = getCurrentMonth();
      const monthToSelect = availableMonths.includes(currentMonth) 
        ? currentMonth 
        : availableMonths[0];
      setSelectedMonth(monthToSelect);
      setHasUserInteractedWithMonthly(true);
    } else {
      // For subsequent clicks, just toggle the dropdown
      setShowMonthDropdown(!showMonthDropdown);
    }
  };

  // Handle manual month selection from dropdown
  const handleMonthSelect = (month) => {
    setSelectedMonth(month);
    setShowMonthDropdown(false);
    setHasUserInteractedWithMonthly(true);
  };

  const leaderboardData = useMemo(() => {
    if (!isOptedIn || !certCodeInFocus) return [];

    const podiumRefs = podiumUsers.map((p) => p.entityRef);
    let candidates = apiLeaderboardData.filter((p) => !podiumRefs.includes(p.entityRef));
    candidates.sort((a, b) => (a.rank || Infinity) - (b.rank || Infinity));

    let rows = candidates.slice(0, 7);
    const currentUserEntry = apiLeaderboardData.find((p) => p.entityRef === userId);

    // If current user is on the leaderboard but not in top 7/podium, append them
    if (
      currentUserEntry &&
      !podiumRefs.includes(userId) &&
      !rows.find((r) => r.entityRef === userId)
    ) {
      rows = [...rows, { ...currentUserEntry, isCurrentUser: true }];
    }

    // If user is opted in but not yet on the leaderboard at all, add a placeholder row
    if (
      isOptedIn &&
      !podiumRefs.includes(userId) &&
      !apiLeaderboardData.find((e) => e.entityRef === userId) &&
      !rows.find((r) => r.isCurrentUser)
    ) {
      const initial = userName.trim() ? userName.charAt(0).toUpperCase() : 'Y';
      rows = [
        ...rows,
        {
          rank: null,
          username: userName.trim() || 'You',
          score: 0,
          initial,
          isCurrentUser: true,
          entityRef: `local-user-${userId}`,
        },
      ];
    }

    rows.sort((a, b) => (a.rank || Infinity) - (b.rank || Infinity));
    return rows;
  }, [apiLeaderboardData, podiumUsers, isOptedIn, certCodeInFocus, userName, userId]);

  // Render the top‐3 podium
  const renderPodium = () => {
    if (podiumUsers.length === 0 && isOptedIn) return null;

    const getPlayer = (pos) => podiumUsers.find((p) => p.rank === pos);
    const first = getPlayer(1);
    const second = getPlayer(2);
    const third = getPlayer(3);

    return (
      <div className="podium-main-section">
        {second ? (
          <div className={`podium-item podium-item-rank2 ${second.isCurrentUser ? 'current-user-podium' : ''}`}>
            <div className="podium-user-card">
              <div className="podium-avatar-circle">{second.initial}</div>
              <div className="podium-username">{second.username}</div>
              <div className="podium-score-display">{Math.round(second.score * 100)}%</div>
            </div>
            <div className="podium-block">
              <span className="podium-rank-text">2</span>
            </div>
          </div>
        ) : first || third ? (
          <div className="podium-item-placeholder podium-item-rank2" />
        ) : null}

        {first ? (
          <div className={`podium-item podium-item-rank1 ${first.isCurrentUser ? 'current-user-podium' : ''}`}>
            <div className="podium-user-card">
              <div className="podium-avatar-circle">{first.initial}</div>
              <div className="podium-username">{first.username}</div>
              <div className="podium-score-display">{Math.round(first.score * 100)}%</div>
            </div>
            <div className="podium-block">
              <span className="podium-rank-text">1</span>
            </div>
          </div>
        ) : second || third ? (
          <div className="podium-item-placeholder podium-item-rank1 podium-item-rank1-placeholder" />
        ) : null}

        {third ? (
          <div className={`podium-item podium-item-rank3 ${third.isCurrentUser ? 'current-user-podium' : ''}`}>
            <div className="podium-user-card">
              <div className="podium-avatar-circle">{third.initial}</div>
              <div className="podium-username">{third.username}</div>
              <div className="podium-score-display">{Math.round(third.score * 100)}%</div>
            </div>
            <div className="podium-block">
              <span className="podium-rank-text">3</span>
            </div>
          </div>
        ) : first || second ? (
          <div className="podium-item-placeholder podium-item-rank3" />
        ) : null}
      </div>
    );
  };

  // Main content: loading / error / no-cert / opt-out / actual leaderboard
  const renderContent = () => {
    if (loading) return <p className="loading-message">Loading leaderboard...</p>;
    if (error) return <p className="error-message">{error}</p>;
    if (!certCodeInFocus || !userId) {
      return <p className="no-data-message">Select a certification to view the leaderboard.</p>;
    }
    if (!isOptedIn) {
      return (
        <div className="leaderboard-opted-out-placeholder">
          <Image
            src="/icon/Demo-image.png"
            alt="Leaderboard Opted Out"
            className="leaderboard-opted-out-image"
          />
          <p className="leaderboard-opted-out-message">
            You are not opted in for <strong>{certCodeInFocus}</strong>. Toggle above to opt in and show your rank!
          </p>
        </div>
      );
    }

    return (
      <>
        {renderPodium()}

        {leaderboardData.length > 0 ? (
          <table className="leaderboard-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>User Name</th>
                <th>Score</th>
              </tr>
            </thead>
            <tbody>
              {leaderboardData.map((entry, idx) => {
                const rowClass = entry.isCurrentUser ? 'current-user-row' : '';
                const key = `${entry.entityRef}-${entry.rank ?? 'local'}-${idx}`;
                return (
                  <tr key={key} className={rowClass}>
                    <td data-label="Rank" className="rank">
                      {entry.rank ?? '–'}
                    </td>
                    <td data-label="User Name" className="avatar">
                      <div className="avatar-circle">{entry.initial}</div>
                      <div className="user-info">{entry.username}</div>
                    </td>
                    <td data-label="Score" className="score">
                      {`${Math.round(entry.score * 100)}%`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="no-data-message">
            {podiumUsers.length > 0
              ? 'No other ranked users yet.'
              : `No leaderboard data available for ${certCodeInFocus}${selectedMonth ? ` in ${selectedMonth}` : ''}. Be the first!`}
          </p>
        )}
      </>
    );
  };

  const theme = certCodeInFocus ? certCodeInFocus.toLowerCase() : 'default';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMonthDropdown && !event.target.closest('.period-monthly-container')) {
        setShowMonthDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMonthDropdown]);

  return (
    <div className="leaderboard-feature-area" data-certification-theme={theme}>
      <div className="leaderboard-opt-toggle-section">
        <label className="toggle-switch">
          <div className="toggle-slider-container">
            <input
              type="checkbox"
              checked={isOptedIn}
              onChange={handleToggle}
              disabled={!certCodeInFocus || !userId || loading}
            />
            <span className="toggle-slider" />
          </div>
          <span className="toggle-label">{isOptedIn ? 'Opted In' : 'Opt In'}</span>
        </label>
        <p className="opt-in-description">
          Toggle to {isOptedIn ? 'opt-out of and hide' : 'opt-in and show'} your rank on the{' '}
          <strong>{certCodeInFocus || 'selected certification'}</strong> leaderboard.
        </p>
      </div>
      <div className="leaderboard-opt-toggle-section">
        <div className="leaderboard-period-selector">
          <div className="period-row">
            <button
              className={`period-button ${!selectedMonth ? 'active' : ''}`}
              onClick={() => {
                setSelectedMonth('');
                setShowMonthDropdown(false);
                setHasUserInteractedWithMonthly(true);
              }}
              disabled={!certCodeInFocus || !userId || !isOptedIn}
            >
              Overall
            </button>
            <div className="period-monthly-container">
              <button
                className={`period-button ${selectedMonth ? 'active' : ''}`}
                onClick={handleMonthlyClick}
                disabled={!certCodeInFocus || !userId || !isOptedIn}
                aria-expanded={showMonthDropdown}
                aria-controls="month-dropdown"
              >
                Monthly {selectedMonth && `(${selectedMonth})`}
              </button>
              {showMonthDropdown && (
                <div id="month-dropdown" className="month-dropdown">
                  <ul>
                    {(() => {
                      if (!availableMonths.length) return <li disabled>No months available</li>;

                      // Group months by year
                      const grouped = availableMonths.reduce((acc, m) => {
                        const [year] = m.split('-');
                        if (!acc[year]) acc[year] = [];
                        acc[year].push(m);
                        return acc;
                      }, {});

                      // Sort years descending (newest first)
                      const years = Object.keys(grouped).sort((a, b) => b - a);

                      return years.map((year) => (
                        <React.Fragment key={year}>
                          <li
                            style={{
                              fontWeight: 'bold',
                              backgroundColor: expandedYear === Number(year) ? '#f7f7f7' : '',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedYear((prev) => (prev === Number(year) ? null : Number(year)));
                            }}
                          >
                            {year}
                          </li>
                          {expandedYear === Number(year) &&
                            grouped[year].map((month) => (
                              <li
                                key={month}
                                onClick={() => handleMonthSelect(month)}
                                className={month === selectedMonth ? 'selected-month' : ''}
                              >
                                {month}
                              </li>
                            ))}
                        </React.Fragment>
                      ));
                    })()}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="leaderboard-container">
        <div className="leaderboard-header">
          <h2 className="leaderboard-title">
            Leaderboard {selectedMonth ? `(${selectedMonth})` : ''}
          </h2>
          {certCodeInFocus && (
            <div className="certification-badge">
              <span>{certCodeInFocus.toUpperCase()}</span>
            </div>
          )}
        </div>
        {renderContent()}
      </div>

      {/** Name-entry Modal */}
      {showNameModal && certCodeInFocus && userId && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3 className="modal-title">Enter Your Name for {certCodeInFocus} Leaderboard</h3>

            <div className="modal-input-wrapper">
              <input
                type="text"
                value={userName}
                onChange={(e) => {
                  const val = e.target.value;
                  setUserName(val);
                  if (!val.trim()) {
                    setUserNameError('Please enter your name.');
                  } else if (/[^a-zA-Z\s]/.test(val)) {
                    setUserNameError('Username can only contain letters and spaces.');
                  } else {
                    setUserNameError('');
                  }
                }}
                placeholder="Your Display Name"
                className={`modal-input ${userNameError.includes('numbers') ? 'input-error-numeric' : ''}`}
                autoFocus
              />
              {userName.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setUserName('');
                    setUserNameError('');
                  }}
                  className="modal-input-clear-button"
                  aria-label="Clear username input"
                >
                  ×
                </button>
              )}
            </div>
            {userNameError && <p className="error-text">{userNameError}</p>}
            <div className="modal-terms-agreement">
              <label className="terms-checkbox-label">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => {
                    setAgreedToTerms(e.target.checked);
                    if (e.target.checked && userNameError === 'Please agree to Terms & Conditions.') {
                      setUserNameError('');
                    }
                  }}
                  className="terms-checkbox"
                />
                <span className="terms-text">
                  I agree to the{' '}
                  <a
                    href="./terms-and-conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="terms-link"
                  >
                    Terms and Conditions
                  </a>{' '}
                  of Wanna Practice
                </span>
              </label>
            </div>

            <div className="modal-actions">
              <button
                onClick={submitNameAndOptIn}
                className="modal-button modal-submit-button"
                disabled={loading || !agreedToTerms || !userName.trim() || userNameError.includes('numbers')}
              >
                {loading ? 'Submitting...' : 'Submit & Opt In'}
              </button>
              <button
                onClick={cancelOptIn}
                className="modal-button modal-cancel-button"
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/** Opt-out Confirmation Modal */}
      {showOptOutConfirm && certCodeInFocus && userId && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3 className="modal-title">Opt-out of {certCodeInFocus} Leaderboard?</h3>
            <p className="modal-subtext">
              Your name and score will be removed from public view. You can always opt back in later.
            </p>
            <div className="modal-actions">
              <button
                onClick={confirmOptOut}
                className="modal-button modal-cancel-button opt-out-confirm-button"
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Yes, Opt Out'}
              </button>
              <button onClick={cancelOptOut} className="modal-button modal-submit-button" disabled={loading}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaderBoard;
